import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft, Users, Clock, DollarSign, Loader2,
  Bell, BellRing, RefreshCw, UtensilsCrossed, XCircle, CheckCircle2, Printer,
  History, CalendarIcon, Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface TabItemAddon {
  id?: string;
  name: string;
  price: number;
  qty: number;
}

interface TabItem {
  id: string;
  product_name: string;
  product_id: string | null;
  quantity: number;
  unit_price: number;
  addons: any;
  notes: string | null;
  session_id: string | null;
  created_at: string;
}

const getItemAddons = (item: TabItem): TabItemAddon[] =>
  Array.isArray(item.addons) ? item.addons : [];

const getItemAddonTotal = (item: TabItem) =>
  getItemAddons(item).reduce((s, a) => s + (a.price || 0) * (a.qty || 1), 0);

const getItemLineTotal = (item: TabItem) =>
  (item.unit_price + getItemAddonTotal(item)) * item.quantity;

interface ActiveTab {
  id: string;
  table_number: number;
  status: string;
  total: number;
  created_at: string;
  closed_at: string | null;
  waiter_requested_at: string | null;
  items: TabItem[];
}

const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

const getElapsedTime = (created: string) => {
  const diff = Math.floor((Date.now() - new Date(created).getTime()) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
};

const getDuration = (created: string, closed: string | null) => {
  const end = closed ? new Date(closed).getTime() : Date.now();
  const diff = Math.floor((end - new Date(created).getTime()) / 1000);
  const hours = Math.floor(diff / 3600);
  const minutes = Math.floor((diff % 3600) / 60);
  if (hours > 0) return `${hours}h ${minutes}min`;
  return `${minutes}min`;
};

const MesasAtivas = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [restaurantId, setRestaurantId] = useState<string | null>(null);
  const [tabs, setTabs] = useState<ActiveTab[]>([]);
  const [now, setNow] = useState(Date.now());
  const [closingTab, setClosingTab] = useState<ActiveTab | null>(null);
  const [activeView, setActiveView] = useState("ativas");

  // History state
  const [historyTabs, setHistoryTabs] = useState<ActiveTab[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDateFrom, setHistoryDateFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return d.toISOString().split("T")[0];
  });
  const [historyDateTo, setHistoryDateTo] = useState(() => new Date().toISOString().split("T")[0]);
  const [historyTableFilter, setHistoryTableFilter] = useState("");
  const [expandedHistory, setExpandedHistory] = useState<string | null>(null);

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchTabs = useCallback(async (restId: string) => {
    const { data: tabsData } = await supabase
      .from("table_tabs")
      .select("*")
      .eq("restaurant_id", restId)
      .eq("status", "open")
      .order("table_number", { ascending: true });

    if (!tabsData || tabsData.length === 0) {
      setTabs([]);
      return;
    }

    const tabIds = tabsData.map((t) => t.id);
    const { data: itemsData } = await supabase
      .from("table_tab_items")
      .select("*")
      .in("tab_id", tabIds)
      .order("created_at", { ascending: true });

    const enriched: ActiveTab[] = tabsData.map((t) => ({
      ...t,
      items: (itemsData || []).filter((i) => i.tab_id === t.id),
    }));

    setTabs(enriched);
  }, []);

  const fetchHistory = useCallback(async (restId: string) => {
    setHistoryLoading(true);
    let query = supabase
      .from("table_tabs")
      .select("*")
      .eq("restaurant_id", restId)
      .eq("status", "closed")
      .gte("closed_at", `${historyDateFrom}T00:00:00`)
      .lte("closed_at", `${historyDateTo}T23:59:59`)
      .order("closed_at", { ascending: false })
      .limit(100);

    if (historyTableFilter) {
      query = query.eq("table_number", parseInt(historyTableFilter));
    }

    const { data: tabsData } = await query;

    if (!tabsData || tabsData.length === 0) {
      setHistoryTabs([]);
      setHistoryLoading(false);
      return;
    }

    const tabIds = tabsData.map((t) => t.id);
    const { data: itemsData } = await supabase
      .from("table_tab_items")
      .select("*")
      .in("tab_id", tabIds)
      .order("created_at", { ascending: true });

    const enriched: ActiveTab[] = tabsData.map((t) => ({
      ...t,
      items: (itemsData || []).filter((i) => i.tab_id === t.id),
    }));

    setHistoryTabs(enriched);
    setHistoryLoading(false);
  }, [historyDateFrom, historyDateTo, historyTableFilter]);

  useEffect(() => {
    const init = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { navigate("/login"); return; }
      
      const impersonateId = sessionStorage.getItem("admin_impersonate_restaurant_id");
      let rest: any = null;
      
      if (impersonateId) {
        const { data } = await supabase
          .from("restaurants").select("id").eq("id", impersonateId).maybeSingle();
        rest = data;
      } else {
        const { data } = await supabase
          .from("restaurants").select("id").eq("owner_id", user.id).maybeSingle();
        rest = data;
      }
      
      if (!rest) { navigate("/dashboard"); return; }
      setRestaurantId(rest.id);
      await fetchTabs(rest.id);
      setLoading(false);
    };
    init();
  }, [navigate, fetchTabs]);

  // Fetch history when tab switches to history or filters change
  useEffect(() => {
    if (activeView === "historico" && restaurantId) {
      fetchHistory(restaurantId);
    }
  }, [activeView, restaurantId, fetchHistory]);

  // Realtime subscription
  useEffect(() => {
    if (!restaurantId) return;

    const channel = supabase
      .channel("active-tabs-realtime")
      .on("postgres_changes", { event: "*", schema: "public", table: "table_tabs" }, () => {
        fetchTabs(restaurantId);
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "table_tab_items" }, () => {
        fetchTabs(restaurantId);
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [restaurantId, fetchTabs]);

   const totalRevenue = tabs.reduce((sum, t) => {
    const itemsTotal = t.items.reduce((s, i) => s + getItemLineTotal(i), 0);
    return sum + itemsTotal;
  }, 0);

  const waiterRequestCount = tabs.filter((t) => t.waiter_requested_at).length;

  const handleDismissWaiter = async (tabId: string) => {
    await supabase
      .from("table_tabs")
      .update({ waiter_requested_at: null })
      .eq("id", tabId);
    toast({ title: "Chamado do garçom atendido" });
  };

  const printReceipt = (tab: ActiveTab) => {
    const itemsTotal = tab.items.reduce((s, i) => s + getItemLineTotal(i), 0);
    const printDate = new Date();
    const dateStr = printDate.toLocaleDateString("pt-BR");
    const timeStr = printDate.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

    const itemsHtml = tab.items
      .map(
        (i) => {
          const addons = getItemAddons(i);
          const addonsHtml = addons.length > 0
            ? addons.map(a => `<tr><td style="text-align:left;padding:0 0 0 16px;font-size:11px;color:#666">+ ${a.name}${a.qty > 1 ? ` (x${a.qty})` : ''} ${formatCurrency(a.price * (a.qty || 1))}</td><td></td></tr>`).join('')
            : '';
          return `<tr>
            <td style="text-align:left;padding:2px 0">${i.quantity}x ${i.product_name}</td>
            <td style="text-align:right;padding:2px 0">${formatCurrency(getItemLineTotal(i))}</td>
          </tr>${addonsHtml}`;
        }
      )
      .join("");

    const win = window.open("", "_blank", "width=400,height=600");
    if (!win) return;
    win.document.write(`
      <html><head><title>Comanda Mesa ${tab.table_number}</title>
      <style>
        *{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Courier New',monospace;padding:10px;max-width:300px;margin:0 auto;font-size:12px}
        .center{text-align:center}
        .divider{border-top:1px dashed #000;margin:6px 0}
        table{width:100%;border-collapse:collapse}
        td{padding:2px 0;vertical-align:top}
        .total-row td{border-top:1px solid #000;padding-top:6px;font-weight:bold;font-size:14px}
        @media print{.no-print{display:none}body{padding:0}}
      </style>
      </head><body>
        <div class="center">
          <strong style="font-size:16px">COMANDA</strong><br/>
          <span>Mesa ${tab.table_number}</span><br/>
          <span>${dateStr} ${timeStr}</span>
        </div>
        <div class="divider"></div>
        <table>
          <tr><td><strong>Item</strong></td><td style="text-align:right"><strong>Valor</strong></td></tr>
          ${itemsHtml}
        </table>
        <div class="divider"></div>
        <table>
          <tr class="total-row">
            <td>TOTAL</td>
            <td style="text-align:right">${formatCurrency(itemsTotal)}</td>
          </tr>
        </table>
        <div class="divider"></div>
        <div class="center" style="margin-top:4px">
          <span>Permanência: ${getDuration(tab.created_at, tab.closed_at)}</span><br/>
          <span style="font-size:10px;margin-top:4px;display:block">Obrigado pela preferência!</span>
        </div>
        <div class="center no-print" style="margin-top:12px">
          <button onclick="window.print()" style="padding:8px 20px;cursor:pointer;border:1px solid #ccc;border-radius:6px;background:white;font-size:14px">Imprimir</button>
        </div>
      </body></html>
    `);
    win.document.close();
  };

  const handleCloseTab = async (tab: ActiveTab) => {
    const itemsTotal = tab.items.reduce((s, i) => s + getItemLineTotal(i), 0);

    // Close the tab — orders were already created when items were added
    const { error } = await supabase
      .from("table_tabs")
      .update({ status: "closed", closed_at: new Date().toISOString(), total: itemsTotal })
      .eq("id", tab.id);

    if (error) {
      toast({ title: "Erro ao fechar comanda", description: error.message, variant: "destructive" });
      return;
    }

    // Mark all dine_in orders for this table as completed so the table is fully freed
    await supabase
      .from("orders")
      .update({ status: "completed" as any, payment_status: "paid" })
      .eq("restaurant_id", restaurantId!)
      .eq("table_number", tab.table_number)
      .in("status", ["received", "preparing", "ready"]);

    setClosingTab(null);
    // Refresh active tabs list immediately
    if (restaurantId) fetchTabs(restaurantId);
    toast({ title: `Mesa ${tab.table_number} liberada`, description: `Total: ${formatCurrency(itemsTotal)} — pronta para novo atendimento` });
  };

  const historyTotalRevenue = historyTabs.reduce((sum, t) => sum + Number(t.total), 0);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b border-border">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-foreground flex items-center gap-2">
                <UtensilsCrossed className="h-5 w-5 text-primary" />
                Mesas
              </h1>
              <p className="text-xs text-muted-foreground">
                {tabs.length} {tabs.length === 1 ? "mesa aberta" : "mesas abertas"}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {waiterRequestCount > 0 && (
              <Badge variant="destructive" className="animate-pulse flex items-center gap-1">
                <BellRing className="h-3 w-3" />
                {waiterRequestCount} chamado{waiterRequestCount > 1 ? "s" : ""}
              </Badge>
            )}
            <Button variant="outline" size="sm" onClick={() => {
              if (restaurantId) {
                fetchTabs(restaurantId);
                if (activeView === "historico") fetchHistory(restaurantId);
              }
            }}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <Tabs value={activeView} onValueChange={setActiveView}>
          <TabsList className="w-full sm:w-auto">
            <TabsTrigger value="ativas" className="flex items-center gap-2">
              <UtensilsCrossed className="h-4 w-4" />
              Ativas
              {tabs.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">{tabs.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="historico" className="flex items-center gap-2">
              <History className="h-4 w-4" />
              Histórico
            </TabsTrigger>
          </TabsList>

          {/* ===== ACTIVE TABS ===== */}
          <TabsContent value="ativas" className="space-y-6 mt-4">
            {/* Summary cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <Users className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Mesas Abertas</p>
                    <p className="text-2xl font-bold text-foreground">{tabs.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total em Aberto</p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(totalRevenue)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-warning/10 flex items-center justify-center">
                    <Bell className="h-5 w-5 text-warning" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Chamados Pendentes</p>
                    <p className="text-2xl font-bold text-foreground">{waiterRequestCount}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Table cards */}
            {tabs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <UtensilsCrossed className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-lg font-medium text-muted-foreground">Nenhuma mesa ativa no momento</p>
                  <p className="text-sm text-muted-foreground mt-1">Quando um cliente escanear o QR Code, a mesa aparecerá aqui.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <AnimatePresence mode="popLayout">
                  {tabs.map((tab) => {
                    const itemsTotal = tab.items.reduce((s, i) => s + getItemLineTotal(i), 0);
                    const uniqueSessions = new Set(tab.items.map((i) => i.session_id).filter(Boolean)).size;
                    const isWaiterCalled = !!tab.waiter_requested_at;

                    return (
                      <motion.div
                        key={tab.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        layout
                      >
                        <Card className={`relative overflow-hidden transition-all ${
                          isWaiterCalled ? "border-destructive ring-2 ring-destructive/20" : ""
                        }`}>
                          {isWaiterCalled && (
                            <div className="absolute top-0 left-0 right-0 bg-destructive text-destructive-foreground text-xs font-medium py-1 px-3 flex items-center justify-between">
                              <span className="flex items-center gap-1">
                                <BellRing className="h-3 w-3 animate-pulse" />
                                Garçom solicitado
                              </span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-5 px-2 text-xs text-destructive-foreground hover:bg-destructive-foreground/20"
                                onClick={() => handleDismissWaiter(tab.id)}
                              >
                                Atender
                              </Button>
                            </div>
                          )}

                          <CardHeader className={`pb-2 ${isWaiterCalled ? "pt-10" : ""}`}>
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg flex items-center gap-2">
                                🍽️ Mesa {tab.table_number}
                              </CardTitle>
                              <Badge variant="outline" className="text-xs">
                                <Clock className="h-3 w-3 mr-1" />
                                {getElapsedTime(tab.created_at)}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                              <span>{tab.items.length} {tab.items.length === 1 ? "item" : "itens"}</span>
                              {uniqueSessions > 0 && (
                                <span className="flex items-center gap-1">
                                  <Users className="h-3 w-3" />
                                  {uniqueSessions} {uniqueSessions === 1 ? "pessoa" : "pessoas"}
                                </span>
                              )}
                            </div>
                          </CardHeader>

                          <CardContent className="pt-0 space-y-3">
                            <div className="space-y-1.5 max-h-40 overflow-y-auto">
                              {tab.items.map((item) => {
                                const addons = getItemAddons(item);
                                return (
                                  <div key={item.id} className="py-1 border-b border-border/50 last:border-0">
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-foreground truncate flex-1">
                                        {item.quantity}x {item.product_name}
                                      </span>
                                      <span className="text-muted-foreground ml-2 whitespace-nowrap">
                                        {formatCurrency(getItemLineTotal(item))}
                                      </span>
                                    </div>
                                    {addons.length > 0 && (
                                      <div className="ml-5 mt-0.5 space-y-0.5">
                                        {addons.map((a, idx) => (
                                          <div key={idx} className="flex items-center justify-between text-xs text-muted-foreground">
                                            <span>+ {a.name}{a.qty > 1 ? ` (x${a.qty})` : ''}</span>
                                            <span>{formatCurrency(a.price * (a.qty || 1))}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t border-border font-semibold">
                              <span className="text-foreground">Total</span>
                              <span className="text-primary text-lg">{formatCurrency(itemsTotal)}</span>
                            </div>

                            <div className="flex gap-2 mt-2">
                              <Button
                                variant="outline"
                                size="sm"
                                className="flex-1"
                                onClick={() => printReceipt(tab)}
                              >
                                <Printer className="h-4 w-4 mr-2" />
                                Imprimir
                              </Button>
                              <Button
                                variant="destructive"
                                size="sm"
                                className="flex-1"
                                onClick={() => setClosingTab(tab)}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Fechar Comanda
                              </Button>
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              </div>
            )}
          </TabsContent>

          {/* ===== HISTORY TAB ===== */}
          <TabsContent value="historico" className="space-y-6 mt-4">
            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row gap-3 items-end">
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Data Início</label>
                    <Input
                      type="date"
                      value={historyDateFrom}
                      onChange={(e) => setHistoryDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="flex-1 space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Data Fim</label>
                    <Input
                      type="date"
                      value={historyDateTo}
                      onChange={(e) => setHistoryDateTo(e.target.value)}
                    />
                  </div>
                  <div className="w-full sm:w-32 space-y-1.5">
                    <label className="text-xs font-medium text-muted-foreground">Mesa</label>
                    <Input
                      type="number"
                      placeholder="Todas"
                      value={historyTableFilter}
                      onChange={(e) => setHistoryTableFilter(e.target.value)}
                      min={1}
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={() => restaurantId && fetchHistory(restaurantId)}
                    className="h-10"
                  >
                    <Search className="h-4 w-4 mr-2" />
                    Buscar
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <History className="h-5 w-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Comandas no Período</p>
                    <p className="text-2xl font-bold text-foreground">{historyTabs.length}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="h-10 w-10 rounded-lg bg-success/10 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-success" />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Faturamento no Período</p>
                    <p className="text-2xl font-bold text-foreground">{formatCurrency(historyTotalRevenue)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* History list */}
            {historyLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
              </div>
            ) : historyTabs.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <History className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-lg font-medium text-muted-foreground">Nenhuma comanda encontrada</p>
                  <p className="text-sm text-muted-foreground mt-1">Ajuste os filtros de data ou mesa para buscar.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {historyTabs.map((tab) => {
                  const isExpanded = expandedHistory === tab.id;
                  const closedDate = tab.closed_at ? new Date(tab.closed_at) : null;

                  return (
                    <Card
                      key={tab.id}
                      className="cursor-pointer transition-all hover:border-primary/30"
                      onClick={() => setExpandedHistory(isExpanded ? null : tab.id)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-lg bg-muted flex items-center justify-center text-sm font-bold text-foreground">
                              {tab.table_number}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-foreground">
                                Mesa {tab.table_number}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                {closedDate?.toLocaleDateString("pt-BR")} às{" "}
                                {closedDate?.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                                {" · "}
                                {getDuration(tab.created_at, tab.closed_at)}
                                {" · "}
                                {tab.items.length} {tab.items.length === 1 ? "item" : "itens"}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-foreground">
                              {formatCurrency(Number(tab.total))}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8"
                              onClick={(e) => {
                                e.stopPropagation();
                                printReceipt(tab);
                              }}
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Expanded items */}
                        {isExpanded && tab.items.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-border space-y-1.5">
                            {tab.items.map((item) => {
                              const addons = getItemAddons(item);
                              return (
                                <div key={item.id}>
                                  <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                      {item.quantity}x {item.product_name}
                                    </span>
                                    <span className="text-muted-foreground">
                                      {formatCurrency(getItemLineTotal(item))}
                                    </span>
                                  </div>
                                  {addons.length > 0 && (
                                    <div className="ml-5 space-y-0.5">
                                      {addons.map((a, idx) => (
                                        <div key={idx} className="flex justify-between text-xs text-muted-foreground/70">
                                          <span>+ {a.name}{a.qty > 1 ? ` (x${a.qty})` : ''}</span>
                                          <span>{formatCurrency(a.price * (a.qty || 1))}</span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Close tab confirmation dialog */}
      <AlertDialog open={!!closingTab} onOpenChange={(open) => !open && setClosingTab(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fechar Comanda — Mesa {closingTab?.table_number}</AlertDialogTitle>
            <AlertDialogDescription>
              {closingTab && (
                <div className="space-y-2 mt-2">
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {closingTab.items.map((item) => {
                      const addons = getItemAddons(item);
                      return (
                        <div key={item.id}>
                          <div className="flex justify-between text-sm">
                            <span>{item.quantity}x {item.product_name}</span>
                            <span>{formatCurrency(getItemLineTotal(item))}</span>
                          </div>
                          {addons.length > 0 && (
                            <div className="ml-5 space-y-0.5">
                              {addons.map((a, idx) => (
                                <div key={idx} className="flex justify-between text-xs text-muted-foreground">
                                  <span>+ {a.name}{a.qty > 1 ? ` (x${a.qty})` : ''}</span>
                                  <span>{formatCurrency(a.price * (a.qty || 1))}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-border text-foreground">
                    <span>Total</span>
                    <span>{formatCurrency(closingTab.items.reduce((s, i) => s + getItemLineTotal(i), 0))}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Um pedido será criado automaticamente e a comanda será encerrada.
                  </p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <Button
              variant="outline"
              onClick={() => closingTab && printReceipt(closingTab)}
            >
              <Printer className="h-4 w-4 mr-2" />
              Imprimir Cupom
            </Button>
            <AlertDialogAction onClick={() => closingTab && handleCloseTab(closingTab)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              <CheckCircle2 className="h-4 w-4 mr-2" />
              Confirmar Fechamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MesasAtivas;
